import { Spacing } from '@persona-id/design-tokens';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import EditAndRulesBlock from 'dashboard/apps/DashboardLayoutEditor/CaseConditionalLogicEditor/EditAndRulesBlock';
import FormError from 'dashboard/components/FormControls/FormError';
import { ConditionalRule, LogicBlockOrV2 } from 'dashboard/lib/workflows';
import { WorkflowOperatorKey } from 'dashboard/models/workflow';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { FieldSchemaType } from 'lib/fields';
import { JsonSchema } from 'lib/json-schema';

const AndRules = styled.div`
  background: #f4f4f6;
  margin-bottom: ${Spacing[2]};
  padding: ${Spacing[4]} ${Spacing[2]};
  border-radius: ${Spacing[1]};
`;

const AddAndRuleButton = styled(Button)`
  width: 40px;
  height: 24px;
  color: black;
  background: #d7d8df;
  margin-top: ${Spacing[4]};
  margin-left: ${Spacing[2]};
`;

const AddOrRuleButton = styled(Button)`
  width: 96px;
  height: 32px;
  color: black;
  background: #d7d8df;
  margin-top: ${Spacing[4]};
`;

export interface EditRulesBlockProps {
  rules: LogicBlockOrV2;
  jsonSchema: JsonSchema;
  updateRule: (orIndex: number, andIndex: number) => (rule: ConditionalRule) => void;
  deleteRule: (orIndex: number, andIndex: number) => void;
  prefixText: string;
  editable: boolean;
  enableFieldValueDropdown: boolean;
  addAndRule: (orIndex: number) => void;
  operatorsMap: Partial<Record<FieldSchemaType, WorkflowOperatorKey[]>>;
  addOrRule: () => void;
  enableValidation: boolean;
  keyPrefix: string;
  setConditionalValidity: (valid: boolean) => void;
  validateRule: (rule: ConditionalRule) => boolean;
}

const EditRulesBlock = ({
  rules,
  jsonSchema,
  operatorsMap,
  updateRule,
  deleteRule,
  prefixText,
  enableFieldValueDropdown,
  keyPrefix,
  addAndRule,
  addOrRule,
  setConditionalValidity,
  enableValidation,
  editable = true,
  validateRule,
}: EditRulesBlockProps) => {
  const [orRulesValidityMap, setOrRulesValidityMap] = useState(
    rules.or.reduce(
      (map, andRules, orIndex) => {
        return {
          ...map,
          [orIndex]: andRules.and.every((baseRule, _) => validateRule(baseRule)),
        };
      },
      {} as Record<number, boolean>,
    ),
  );
  const [areAllRulesValid, setAreAllRulesValid] = useState(
    Object.values(orRulesValidityMap).every((valid) => valid),
  );
  const showInvalidMessage = enableValidation && !areAllRulesValid;

  // Always stay up to date on which rules are valid
  useEffect(() => {
    const newValidityMap = rules.or.reduce(
      (map, andRules, orIndex) => {
        return {
          ...map,
          [orIndex]: andRules.and.every((baseRule, _) => validateRule(baseRule)),
        };
      },
      {} as Record<number, boolean>,
    );
    setOrRulesValidityMap(newValidityMap);
    const valid = Object.values(newValidityMap).every((valid) => valid);
    setAreAllRulesValid(valid);
    setConditionalValidity(valid);
  }, [rules]);

  return (
    <>
      {rules.or.map((andRules, orIndex) => {
        // for Every Or rules block, start the block with "Show if" if it is the first rule
        // or "Or" for every following rule block
        //
        // Show if   LogicBlock
        // Or        LogicBlock
        // Or        LogicBlock
        return (
          <AndRules key={`${keyPrefix}_${orIndex}_AndRules`}>
            <EditAndRulesBlock
              andRules={andRules.and}
              editable={editable}
              orPrefixText={orIndex === 0 ? prefixText : 'Or'}
              jsonSchema={jsonSchema}
              enableFieldValueDropdown={enableFieldValueDropdown}
              operatorsMap={operatorsMap}
              orIndex={orIndex}
              enableValidation={enableValidation}
              updateRule={updateRule}
              deleteRule={deleteRule}
              validateRule={validateRule}
            />
            {editable && (
              <AddAndRuleButton variant='basic' width='icon' onClick={() => addAndRule(orIndex)}>
                And
              </AddAndRuleButton>
            )}
          </AndRules>
        );
      })}
      {showInvalidMessage && <FormError>Conditions are not valid</FormError>}
      {editable && (
        <>
          <Spacer height={1} />
          <AddOrRuleButton variant='basic' size='sm' onClick={addOrRule}>
            Or group
          </AddOrRuleButton>
        </>
      )}
    </>
  );
};

export default EditRulesBlock;
